import { defineStore } from 'pinia'
import { computed, readonly, ref } from 'vue'

import { CampaignTweetPostApiError } from '../api/campaignTweetPostApiError'
import { getCampaignTweetPosts } from '../api/getCampaignTweetPosts'
import { postCampaignTweetPosts } from '../api/postCampaignTweetPosts'

export type CampaignTweetPostMode = 'create' | 'edit'

export type CampaignTweetPostSaveResult = { ok: true } | { ok: false; type: string }

// 取得前・取得失敗時に使う登録上限数。groups.max_tweet_post_url_count の DB 既定値と揃える。
export const DEFAULT_MAX_TWEET_POST_URL_COUNT = 10

export type CampaignTweetPostDialogState =
  | 'createEmpty' // 新規・URL 未入力
  | 'createFilled' // 新規・URL 入力済み
  | 'editSingle' // 編集・1 件
  | 'editMultiple' // 編集・複数
  | 'saving' // 保存中（loading）
  | 'locked' // 開催終了(最終集計中)・閲覧のみ

export const useCampaignTweetPostStore = defineStore('campaignTweetPostStore', () => {
  const urls = ref<string[]>([])
  const deletableUrls = ref<string[]>([])
  const postIdByUrl = ref<Map<string, string>>(new Map())
  const maxTweetPostUrlCount = ref(DEFAULT_MAX_TWEET_POST_URL_COUNT)
  const fetching = ref(false)
  const saving = ref(false)
  const mode = ref<CampaignTweetPostMode>('create')
  const isLocked = ref(false)

  const setMode = (next: CampaignTweetPostMode): void => {
    mode.value = next
  }

  const setUrls = (next: string[]): void => {
    urls.value = [...next]
  }

  const setLocked = (locked: boolean): void => {
    isLocked.value = locked
  }

  const fetchTweetPosts = async (campaignId: number): Promise<void> => {
    if (fetching.value) return
    fetching.value = true
    try {
      const data = await getCampaignTweetPosts(campaignId)
      urls.value = data.posts.map(post => post.url)
      deletableUrls.value = data.posts.filter(post => post.deletable).map(post => post.url)
      postIdByUrl.value = new Map(data.posts.map(post => [post.url, post.postId]))
      maxTweetPostUrlCount.value = data.maxTweetPostUrlCount
    } finally {
      fetching.value = false
    }
  }

  // 例外の種別を取り出す。CampaignTweetPostApiError なら error.type、それ以外は 'UNKNOWN'。
  const resolveErrorType = (error: unknown): string =>
    error instanceof CampaignTweetPostApiError ? error.type : 'UNKNOWN'

  const save = async (campaignId: number, targetUrls: string[]): Promise<CampaignTweetPostSaveResult> => {
    saving.value = true
    try {
      await postCampaignTweetPosts(campaignId, targetUrls)
      return { ok: true }
    } catch (error) {
      return { ok: false, type: resolveErrorType(error) }
    } finally {
      saving.value = false
    }
  }

  const reset = (): void => {
    urls.value = []
    deletableUrls.value = []
    postIdByUrl.value = new Map()
    maxTweetPostUrlCount.value = DEFAULT_MAX_TWEET_POST_URL_COUNT
    fetching.value = false
    saving.value = false
    mode.value = 'create'
    isLocked.value = false
  }

  // 新規・未入力では保存不可（C3-001, F009）。最終集計中は常に保存不可（C3-005, F010）。
  const isSaveDisabled = computed(() => {
    if (isLocked.value) return true
    if (mode.value === 'create') return urls.value.length === 0
    return false
  })

  // 状態の判別（優先順位: 保存中 > 最終集計中 > mode/件数）。
  const dialogState = computed<CampaignTweetPostDialogState>(() => {
    if (saving.value) return 'saving'
    if (isLocked.value) return 'locked'
    if (mode.value === 'create') return urls.value.length === 0 ? 'createEmpty' : 'createFilled'
    return urls.value.length > 1 ? 'editMultiple' : 'editSingle'
  })

  return {
    urls: readonly(urls),
    deletableUrls: readonly(deletableUrls),
    postIdByUrl: readonly(postIdByUrl),
    maxTweetPostUrlCount: readonly(maxTweetPostUrlCount),
    fetching: readonly(fetching),
    saving: readonly(saving),
    mode: readonly(mode),
    isLocked: readonly(isLocked),
    isSaveDisabled,
    dialogState,
    setMode,
    setUrls,
    setLocked,
    fetchTweetPosts,
    save,
    reset
  }
})
